import {MutableRefObject, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {appendQueryString, parseSearch} from "@pg-mono/request";
import {useUserDevice} from "@pg-mono/user-device";
import {validateSelectedRegionsWithAvailableDistrictsAndSurroundings} from "@pg-rp/interactive-regions-map";

import {rpFeatureFlagNewUserFlow} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {getOfferListSubFilterParsed} from "../../offer/helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {useOffersViewsUrl} from "../../offer/hooks/use_offers_views_url";
import {resetPrivilegedOfferList as resetPrivilegedOfferListAction} from "../../offer/list/actions/fetch_privileged_offer_list";
import {ISelectedRegion} from "../../offer/list/actions/offer_list_selected_data_actions";
import {NotificationVariant, OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {useDefaultDistance} from "../../offer/list/hooks/use_default_distance";
import {useOfferListingAbroadView} from "../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {useMapRegionDistrictsAndSurroundings} from "../../offer/map/hooks/use_map_region_districts_and_surroundings";
import {propertyConfiguratorSearchFormFields} from "../../property_configurator/reducers/property_configurator_reducer";
import {hitFiltersInteraction} from "../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../real_estate/utils/convert_search_form_to_filters_tracking";
import {useAppDispatch} from "../../store/hooks";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {fetchAllSearchLists as fetchAllSearchListsAction, IFetchAllSearchListsParams} from "../actions/fetch_search_all_action";
import {resetPlaceList as resetPlaceListAction} from "../actions/fetch_search_places_action";
import {
    resetMultiRegionSearchForm as resetMultiRegionSearchFormAction,
    resetSearchForm as resetSearchFormAction,
    searchFormActions
} from "../actions/search_form_actions";
import {resetActiveDropdownItem as resetActiveDropdownItemAction} from "../actions/set_active_dropdown_item";
import {setPreviousDistance as setPreviousDistanceAction} from "../actions/set_travel_time";
import {GuideTourStep} from "../constants/GuideTourStep";
import {searchFormFields} from "../constants/search_form_constants";
import {SEARCH_FORM_FOCUS_HASH} from "../constants/search_form_focus_hash";
import {searchFormWithDrawerHeight} from "../constants/search_form_with_drawer_height";
import {useCountSearchFilters} from "../hooks/use_count_search_filters";
import {useFilter} from "../hooks/use_filter";
import {useMobileFilters} from "../hooks/use_mobile_filters";
import {useRegionSearchTabs} from "../hooks/use_region_search_tabs";
import useSearchAndFilterUpdate from "../hooks/use_search_and_filter_update";
import {useSearchForm} from "../hooks/use_search_form";
import {useSearchFormSubmit} from "../hooks/use_search_form_submit";
import {useSearchGuideTour} from "../hooks/use_search_guide_tour";
import {filtersModalOpened} from "../slices/filters_visibility_slice";
import {RegionSearchTabsType} from "../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getSearchFiltersByOfferType} from "../utils/get_search_filters_by_offer_type";
import {isRestoredFiltersEnabled} from "../utils/restored_filters";
import {googlePlacesDivId} from "../utils/search_constants";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {SlotOrderType} from "./search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchAutocompleteWithDropdown} from "./search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchAutocompleteWithRegionAdjustments} from "./search_autocomplete/SearchAutocompleteWithRegionAdjustments";
import {HotlineInfo} from "./HotlineInfo";
import {MobileSearchTriggers} from "./MobileSearchTriggers";
import {MobileTopSearchTriggers} from "./MobileTopSearchTriggers";
import {SearchFilters} from "./SearchFilters";

interface IProps {
    latestQuery: ILatestQuery;
    hasParentBeenInitialized?: boolean;
    selectedRegions?: ISelectedRegion[];
    multiRegionLink: () => string;
    listFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    sortActive?: boolean;
    shouldRenderListBackBtn?: boolean;
    blendTop?: string;
    blendHeightDecrease?: string;
    className?: string;
    slotOrder?: SlotOrderType;
    isMap?: boolean;
    showTopFilters?: boolean;
    hideBottomPadding?: boolean;
    disableLocationSearch?: boolean;
    autoFocusOnInit?: boolean;
    disableFilterAutoShowcase?: boolean;
    guideTourName?: string;
    showMobileTopSearchTrigger?: boolean;
}

export const SearchFormWithDrawer = (props: IProps) => {
    const {
        hasParentBeenInitialized = true,
        disableLocationSearch,
        disableFilterAutoShowcase,
        showMobileTopSearchTrigger,
        sortActive,
        isMap,
        shouldRenderListBackBtn,
        blendTop,
        blendHeightDecrease,
        guideTourName,
        className,
        latestQuery,
        showTopFilters,
        autoFocusOnInit,
        selectedRegions,
        slotOrder,
        hideBottomPadding,
        multiRegionLink,
        listFriendlyLink,
        abroadListFriendlyLink
    } = props;

    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const {
        isLocationSearchOpen: isLocationSearchOpenGlobal,
        setLocationSearchOpenStatus,
        toggleLocationSearch,
        addLocationSearchStatusWatcher
    } = useSearchForm();
    const formValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const dispatch = useAppDispatch();
    const history = useHistory();
    const location = useLocation();

    // Check if filters were restored from recent filters - if so, prevent auto-opening location search modal
    const urlParams = parseSearch(location.search);
    const filtersRestored = isRestoredFiltersEnabled(urlParams.restored_filters);
    // Allow manual opening, only prevent auto-opening on initialization
    const isLocationSearchOpen = disableLocationSearch ? false : isLocationSearchOpenGlobal;

    // TODO: setDesktopTypeBlendActive & setDesktopSearchBlendActive need to be refactored,
    // probably display should depend on isLocationSearchOpen and css media query
    const [isDesktopTypeBlendActive, setDesktopTypeBlendActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const searchAutocompleteWrapperRef = useRef<HTMLDivElement>(null);
    const {guideTourCurrentStepId, completeGuideTourStep} = useSearchGuideTour({name: guideTourName});

    const resetSearchForm = (query: Record<string, string>) => dispatch(resetSearchFormAction(query));
    const resetActiveDropdownItem = () => dispatch(resetActiveDropdownItemAction());
    const resetMultiRegionSearchForm = (query: ILatestQuery, locationSearch: string) => dispatch(resetMultiRegionSearchFormAction(query, locationSearch));
    const fetchAllSearchLists = async (searchInput: string, params: IFetchAllSearchListsParams): Promise<SearchTab> =>
        dispatch(fetchAllSearchListsAction(searchInput, params));
    const resetPrivilegedOfferList = () => dispatch(resetPrivilegedOfferListAction());
    const setPreviousDistance = (distance: string | number | undefined) => setPreviousDistanceAction(distance)(dispatch);
    const resetPlaceList = () => dispatch(resetPlaceListAction());

    const formActions = {
        update: (values: Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>) =>
            dispatch(searchFormActions.update(values)),
        replace: (values: Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>) =>
            dispatch(searchFormActions.replace(values)),
        clear: () => dispatch(searchFormActions.clear())
    };

    // Narrowed formActions for components that only need searchFormFields
    const searchFormActionsOnly = {
        update: (values: Partial<SearchFormValuesType>) =>
            formActions.update(values as Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>),
        replace: (values: SearchFormValuesType) =>
            formActions.replace(values as Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>),
        clear: () => formActions.clear()
    };

    const {activeRegionSearchTab, regionSearchTabs, regionSearchTabChangeHandler} = useRegionSearchTabs({
        regionQuery: formValues?.search?.label,
        onSearchTabChange: (tab: RegionSearchTabsType) => {
            if (tab === RegionSearchTabsType.TRAVEL_TIME) {
                setPreviousDistance(latestQuery.distance);
            }
        }
    });

    const {data: mapDistrictsAndSurroundsData, isLoading: areMapDistrictsAndSurroundingsLoading} = useMapRegionDistrictsAndSurroundings({
        selectedRegions
    });

    const scrollIntoSearchAutocompleteWrapper = () => {
        if (searchAutocompleteWrapperRef.current) {
            const marginOffset = 70;
            const elementTop = searchAutocompleteWrapperRef.current.getBoundingClientRect().top;
            const offsetTop = document.documentElement.scrollTop;
            const scrollToPosition = elementTop + offsetTop - marginOffset;

            window.scrollTo({
                top: scrollToPosition,
                behavior: "smooth"
            });
        }
    };

    useEffect(() => {
        if (!rpFeatureFlagNewUserFlow) {
            return;
        }

        // Don't auto-open location search if filters were restored
        if (filtersRestored) {
            return;
        }

        if (!autoFocusOnInit || guideTourCurrentStepId !== GuideTourStep.SELECT_DISTRICTS || areMapDistrictsAndSurroundingsLoading) {
            return;
        }

        // should be opening and scroll into view shortly after the component is mounted
        if (validateSelectedRegionsWithAvailableDistrictsAndSurroundings(selectedRegions || [], mapDistrictsAndSurroundsData?.results)) {
            setLocationSearchOpenStatus(true);
            addLocationSearchStatusWatcher(() => {
                completeGuideTourStep();
            }, false);
        } else {
            completeGuideTourStep();
        }
    }, [guideTourCurrentStepId, areMapDistrictsAndSurroundingsLoading, filtersRestored]);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        formValues,
        delayAutoOpen: rpFeatureFlagNewUserFlow ? guideTourCurrentStepId !== GuideTourStep.SELECT_FILTERS : true,
        onClose: (makeGtmHit?: boolean) => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
            makeGtmHit && hitFiltersInteraction("close", convertSearchFormToFiltersTracking(formValues));
        }
    });
    const isAbroadOffersView = useOfferListingAbroadView();

    const [windowHeight, setWindowHeight] = useState(0);
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (!isMobile) {
            setDesktopSearchBlendActive(isLocationSearchOpen);

            if (autoFocusOnInit && isLocationSearchOpen) {
                scrollIntoSearchAutocompleteWrapper();
            }
        }
    }, [isMobile, isLocationSearchOpen]);

    // handle initial focus on search
    const focusOnSearch = (ref: MutableRefObject<HTMLElement | null>, callback?: () => void) => {
        if (location.hash === SEARCH_FORM_FOCUS_HASH && ref.current?.offsetParent) {
            history.replace({hash: ""});
            setLocationSearchOpenStatus(true);
            callback && callback();
        }
    };
    // focus on search
    const onInputMount = (ref: MutableRefObject<HTMLInputElement | null>) => {
        focusOnSearch(ref, () =>
            setTimeout(() => {
                ref.current?.focus();
            }, 0)
        );
    };
    // and mobile focus on search
    const onMobileSearchTriggerMount = (ref: MutableRefObject<HTMLDivElement | null>) => {
        focusOnSearch(ref);
    };

    useEffect(() => {
        // TODO: This condition is to maintain compatibility with old forms and allow building latestQuery during component's lifecycle.
        // In the future it would be better to rewrite whole form defaults logic.
        if (!hasParentBeenInitialized) {
            return;
        }

        const query = latestQuery as Record<string, string>;

        (async () => {
            if (location.pathname === multiRegionLink()) {
                resetMultiRegionSearchForm(query, location.search);
            } else {
                resetSearchForm(query);
            }
        })();
    }, [hasParentBeenInitialized]);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */
    const onSubmit = useSearchFormSubmit({
        latestQuery,
        listFriendlyLink: ({friendlySlug, offerListSubFilterSlug}) => {
            if (viewType.current === ViewType.OFFER_LIST_MAP && offerListSubFilterSlug) {
                const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilterSlug);
                return appendQueryString(
                    listFriendlyLink({
                        friendlySlug,
                        offerListSubFilterSlug
                    }),
                    subFilterQuery
                );
            }

            return listFriendlyLink({friendlySlug, offerListSubFilterSlug});
        },
        abroadListFriendlyLink: (params) => {
            return abroadListFriendlyLink && abroadListFriendlyLink(params);
        },
        multiRegionLink
    });

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
        }

        formActions.update(values);
    };
    const onSearchSubmit = (userQueryParams?: {distance?: number}) => {
        onSubmit(formValues, userQueryParams);
    };

    const onSearchFiltersSubmit = () => {
        onSubmit(formValues);
    };

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList,
        formValues,
        latestQuery,
        onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => onSubmit(formValues, userQueryParams)
    });

    const enableFiltersAutoOpen = useMemo(
        () => (rpFeatureFlagNewUserFlow ? guideTourCurrentStepId === GuideTourStep.SELECT_FILTERS : false),
        [guideTourCurrentStepId]
    );

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues,
        formActions: searchFormActionsOnly,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => onSubmit(formValues, userQueryParams),
        enableFiltersAutoOpen,
        disableAutoShowcase: disableFilterAutoShowcase,
        onClose: () => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
        }
    });

    useEffect(() => {
        if (enableFiltersAutoOpen && isDrawerOpen) {
            scrollIntoSearchAutocompleteWrapper();
        }
    }, [isDrawerOpen]);

    const filtersButtonCLick = () => {
        dispatch(filtersModalOpened({filtersModalOpened: true}));
        setDrawerOpen(true);
    };

    /**
     * Handle filters
     */
    const searchFiltersConfig = getSearchFiltersByOfferType(formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters} = useCountSearchFilters(formValues as SearchFormValuesType, searchFiltersConfig);

    const {listUrl, mapUrl} = useOffersViewsUrl();

    const onTravelTimeCancel = () => {
        regionSearchTabChangeHandler(RegionSearchTabsType.LOCATION);
        dispatch(resetPlaceList());
    };

    const renderSearchAutocomplete = (onDropdownStatusChange: (isOpen: boolean) => void, isInModal?: boolean) => {
        if (disableLocationSearch) {
            return null;
        }

        return isAbroadOffersView ? (
            <SearchAutocompleteWithDropdown
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={setLocationSearchOpenStatus}
                formProps={{
                    formValues,
                    formActions,
                    fetchAllSearchLists,
                    latestQuery
                }}
                onAfterChange={onAfterChange}
                onSearchSubmit={onSearchSubmit}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                isInModal={isInModal}
                placeholder="Zmień lokalizację"
                showArrowOnMobile
                blockBodyScroll
            />
        ) : (
            <SearchAutocompleteWithRegionAdjustments
                ref={searchAutocompleteWrapperRef}
                activeRegionSearchTab={activeRegionSearchTab}
                defaultDistanceValue={defaultDistanceValue}
                fetchAllSearchLists={fetchAllSearchLists}
                formActions={searchFormActionsOnly}
                formValues={formValues as SearchFormValuesType}
                isInModal={isInModal}
                onAfterChange={onAfterChange}
                onAfterTabSwitch={onAfterTabSwitch}
                onChange={onChange}
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={onDropdownStatusChange}
                onInputMount={onInputMount}
                onSearchSubmit={onSearchSubmit}
                onTravelTimeCancel={onTravelTimeCancel}
                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                slotOrder={slotOrder}
                regionSearchTabChangeHandler={regionSearchTabChangeHandler}
                regionSearchTabs={regionSearchTabs}
                blockBodyScroll
            />
        );
    };

    /**
     * Render
     */
    const defaultDistanceValue = useDefaultDistance(formValues.distance || latestQuery?.distance);
    const mobileFiltersProps = {
        selectedRegionsCount: formValues["search"].regions ? formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleSearchClick: toggleLocationSearch,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: sortActive ? () => setMobileSortActive((prev) => !prev) : null,
        mapUrl: shouldRenderListBackBtn ? mapUrl : undefined,
        listUrl: !shouldRenderListBackBtn ? listUrl : undefined,
        latestQuery,
        onMount: onMobileSearchTriggerMount,
        firstSelectedRegion: formValues.search?.regions?.[0]?.name,
        sortActive
    };

    return (
        <div className={cx(wrapperStyle, className)}>
            <DesktopBlend
                isActive={isDesktopSearchBlendActive || isDesktopTypeBlendActive}
                pageHeight={`${windowHeight}px`}
                top={blendTop || `${searchFormWithDrawerHeight}px`}
                heightDecrease={blendHeightDecrease ? blendHeightDecrease : `${searchFormWithDrawerHeight}px`}
            />

            <Hidden on="desktop" className={css({py: 1.5})}>
                <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
            </Hidden>

            {!isMobile && (
                <div className={searchSectionWrapper}>
                    <Hidden on="mobile">
                        <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
                    </Hidden>

                    <div className={searchFormWrapperStyle}>
                        <div className={searchInputWrapper}>
                            <div id={googlePlacesDivId} />
                            {renderSearchAutocomplete(setLocationSearchOpenStatus)}
                        </div>
                    </div>
                    <div className={filtersButtonsStyle}>
                        <Button size="small" variant="filled_primary" iconLeft={FiltersIcon} className={actionButtonStyle} onClick={filtersButtonCLick}>
                            FILTRY
                            {numberOfSearchFilters > 0 ? (
                                <Badge variant="label_danger" className={filtersCountBadge}>
                                    {numberOfSearchFilters}
                                </Badge>
                            ) : null}
                        </Button>
                        {formValues.search?.regions && <OfferListNotificationsSubscriptionModal variant={NotificationVariant.ICON} />}
                    </div>
                </div>
            )}

            <SearchFilters
                useFilterHook={{isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters}}
                useMobileFilterHook={{isMobileFiltersActive, closeMobileFilterModal, prevFilterState, toggleMobileFilterModal}}
                latestQuery={latestQuery}
                handleSubmit={onSearchSubmit}
                values={formValues as SearchFormValuesType}
                onChange={formActions.update}
                onAfterChange={onAfterChange}
                onSubmit={onSearchFiltersSubmit}
                isAbroad={isAbroadOffersView}
            />

            {isMobile && (
                <>
                    {showTopFilters ? (
                        <MobileTopSearchTriggers
                            forceShowTopSearchTrigger={showMobileTopSearchTrigger}
                            searchFieldPropsValue={formValues.search}
                            {...mobileFiltersProps}
                            isMap={isMap}
                            showLocationSearch
                        />
                    ) : (
                        <MobileSearchTriggers {...mobileFiltersProps} positionFixed hideBottomPadding={hideBottomPadding} />
                    )}
                    {sortActive && (
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                    )}
                    <MobileOptionsModal
                        modalHeading="Wyszukiwanie"
                        isOpen={isLocationSearchOpen}
                        onModalClose={() => {
                            setLocationSearchOpenStatus(false);
                        }}
                    >
                        <div id={googlePlacesDivId} />
                        {renderSearchAutocomplete(setLocationSearchOpenStatus, true)}
                    </MobileOptionsModal>
                </>
            )}
        </div>
    );
};

const searchSectionWrapper = css({
    display: "none",

    md: {
        mx: "auto",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        pt: 2,
        pb: 1,
        gap: 2,
        position: "relative",
        flexGrow: 1,
        flexShrink: 1,
        flexWrap: "wrap"
    }
});

const searchFormWrapperStyle = css({
    display: "flex",
    flex: "1 1"
});

const searchInputWrapper = css({
    display: "none",

    md: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        flex: "1 1"
    }
});

const actionButtonStyle = css({
    width: "18rem",
    overflow: "visible",
    display: "none",

    md: {
        display: "inline-flex"
    }
});

const filtersCountBadge = css({
    position: "absolute",
    right: "-0.5rem",
    top: "-0.5rem"
});

const wrapperStyle = css({
    "&.isInNavigationBar": {
        pb: 0,
        "& > div:nth-of-type(2)": {
            pt: 0
        }
    }
});

const filtersButtonsStyle = css({
    display: "flex",
    gap: 2
});
import {MutableRefObject, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {Hidden} from "@pg-design/grid-module";
import {FiltersIcon} from "@pg-design/icons-module";
import {appendQueryString, parseSearch} from "@pg-mono/request";
import {useUserDevice} from "@pg-mono/user-device";
import {validateSelectedRegionsWithAvailableDistrictsAndSurroundings} from "@pg-rp/interactive-regions-map";

import {rpFeatureFlagNewUserFlow} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {getOfferListSubFilterParsed} from "../../offer/helpers/friendly_offer_list/get_offer_list_sub_filter_parsed";
import {useOffersViewsUrl} from "../../offer/hooks/use_offers_views_url";
import {resetPrivilegedOfferList as resetPrivilegedOfferListAction} from "../../offer/list/actions/fetch_privileged_offer_list";
import {ISelectedRegion} from "../../offer/list/actions/offer_list_selected_data_actions";
import {NotificationVariant, OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {OfferListSortMobile} from "../../offer/list/components/OfferListsSortMobile";
import {SearchSortString} from "../../offer/list/constants/SearchSort";
import {useDefaultDistance} from "../../offer/list/hooks/use_default_distance";
import {useOfferListingAbroadView} from "../../offer/list/hooks/use_offer_listing_abroad_view_check";
import {useMapRegionDistrictsAndSurroundings} from "../../offer/map/hooks/use_map_region_districts_and_surroundings";
import {propertyConfiguratorSearchFormFields} from "../../property_configurator/reducers/property_configurator_reducer";
import {hitFiltersInteraction} from "../../real_estate/tracking/hit_filters_interaction";
import {convertSearchFormToFiltersTracking} from "../../real_estate/utils/convert_search_form_to_filters_tracking";
import {useAppDispatch} from "../../store/hooks";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {fetchAllSearchLists as fetchAllSearchListsAction, IFetchAllSearchListsParams} from "../actions/fetch_search_all_action";
import {resetPlaceList as resetPlaceListAction} from "../actions/fetch_search_places_action";
import {
    resetMultiRegionSearchForm as resetMultiRegionSearchFormAction,
    resetSearchForm as resetSearchFormAction,
    searchFormActions
} from "../actions/search_form_actions";
import {resetActiveDropdownItem as resetActiveDropdownItemAction} from "../actions/set_active_dropdown_item";
import {setPreviousDistance as setPreviousDistanceAction} from "../actions/set_travel_time";
import {GuideTourStep} from "../constants/GuideTourStep";
import {searchFormFields} from "../constants/search_form_constants";
import {SEARCH_FORM_FOCUS_HASH} from "../constants/search_form_focus_hash";
import {searchFormWithDrawerHeight} from "../constants/search_form_with_drawer_height";
import {useCountSearchFilters} from "../hooks/use_count_search_filters";
import {useFilter} from "../hooks/use_filter";
import {useMobileFilters} from "../hooks/use_mobile_filters";
import {useRegionSearchTabs} from "../hooks/use_region_search_tabs";
import useSearchAndFilterUpdate from "../hooks/use_search_and_filter_update";
import {useSearchForm} from "../hooks/use_search_form";
import {useSearchFormSubmit} from "../hooks/use_search_form_submit";
import {useSearchGuideTour} from "../hooks/use_search_guide_tour";
import {filtersModalOpened} from "../slices/filters_visibility_slice";
import {RegionSearchTabsType} from "../types/RegionSearchTabsType";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getSearchFiltersByOfferType} from "../utils/get_search_filters_by_offer_type";
import {isRestoredFiltersEnabled} from "../utils/restored_filters";
import {googlePlacesDivId} from "../utils/search_constants";
import {ISearchAutocompleteFormValues} from "./search_autocomplete/SearchAutocomplete";
import {SlotOrderType} from "./search_autocomplete/SearchAutocompleteInputWithSlot";
import {SearchAutocompleteWithDropdown} from "./search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchAutocompleteWithRegionAdjustments} from "./search_autocomplete/SearchAutocompleteWithRegionAdjustments";
import {HotlineInfo} from "./HotlineInfo";
import {MobileSearchTriggers} from "./MobileSearchTriggers";
import {MobileTopSearchTriggers} from "./MobileTopSearchTriggers";
import {SearchFilters} from "./SearchFilters";

interface IProps {
    latestQuery: ILatestQuery;
    hasParentBeenInitialized?: boolean;
    selectedRegions?: ISelectedRegion[];
    multiRegionLink: () => string;
    listFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    sortActive?: boolean;
    shouldRenderListBackBtn?: boolean;
    blendTop?: string;
    blendHeightDecrease?: string;
    className?: string;
    slotOrder?: SlotOrderType;
    isMap?: boolean;
    showTopFilters?: boolean;
    hideBottomPadding?: boolean;
    disableLocationSearch?: boolean;
    autoFocusOnInit?: boolean;
    disableFilterAutoShowcase?: boolean;
    guideTourName?: string;
    showMobileTopSearchTrigger?: boolean;
}

export const SearchFormWithDrawer = (props: IProps) => {
    const {
        hasParentBeenInitialized = true,
        disableLocationSearch,
        disableFilterAutoShowcase,
        showMobileTopSearchTrigger,
        sortActive,
        isMap,
        shouldRenderListBackBtn,
        blendTop,
        blendHeightDecrease,
        guideTourName,
        className,
        latestQuery,
        showTopFilters,
        autoFocusOnInit,
        selectedRegions,
        slotOrder,
        hideBottomPadding,
        multiRegionLink,
        listFriendlyLink,
        abroadListFriendlyLink
    } = props;

    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const {
        isLocationSearchOpen: isLocationSearchOpenGlobal,
        setLocationSearchOpenStatus,
        toggleLocationSearch,
        addLocationSearchStatusWatcher
    } = useSearchForm();
    const formValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const dispatch = useAppDispatch();
    const history = useHistory();
    const location = useLocation();

    // Check if filters were restored from recent filters - if so, prevent auto-opening location search modal
    const urlParams = parseSearch(location.search);
    const filtersRestored = isRestoredFiltersEnabled(urlParams.restored_filters);
    // Allow manual opening, only prevent auto-opening on initialization
    const isLocationSearchOpen = disableLocationSearch ? false : isLocationSearchOpenGlobal;

    // TODO: setDesktopTypeBlendActive & setDesktopSearchBlendActive need to be refactored,
    // probably display should depend on isLocationSearchOpen and css media query
    const [isDesktopTypeBlendActive, setDesktopTypeBlendActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const searchAutocompleteWrapperRef = useRef<HTMLDivElement>(null);
    const {guideTourCurrentStepId, completeGuideTourStep} = useSearchGuideTour({name: guideTourName});

    const resetSearchForm = (query: Record<string, string>) => dispatch(resetSearchFormAction(query));
    const resetActiveDropdownItem = () => dispatch(resetActiveDropdownItemAction());
    const resetMultiRegionSearchForm = (query: ILatestQuery, locationSearch: string) => dispatch(resetMultiRegionSearchFormAction(query, locationSearch));
    const fetchAllSearchLists = async (searchInput: string, params: IFetchAllSearchListsParams): Promise<SearchTab> =>
        dispatch(fetchAllSearchListsAction(searchInput, params));
    const resetPrivilegedOfferList = () => dispatch(resetPrivilegedOfferListAction());
    const setPreviousDistance = (distance: string | number | undefined) => setPreviousDistanceAction(distance)(dispatch);
    const resetPlaceList = () => dispatch(resetPlaceListAction());

    const formActions = {
        update: (values: Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>) =>
            dispatch(searchFormActions.update(values)),
        replace: (values: Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>) =>
            dispatch(searchFormActions.replace(values)),
        clear: () => dispatch(searchFormActions.clear())
    };

    // Narrowed formActions for components that only need searchFormFields
    const searchFormActionsOnly = {
        update: (values: Partial<SearchFormValuesType>) =>
            formActions.update(values as Partial<Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>>),
        replace: (values: SearchFormValuesType) =>
            formActions.replace(values as Record<keyof typeof searchFormFields | keyof typeof propertyConfiguratorSearchFormFields, unknown>),
        clear: () => formActions.clear()
    };

    const {activeRegionSearchTab, regionSearchTabs, regionSearchTabChangeHandler} = useRegionSearchTabs({
        regionQuery: formValues?.search?.label,
        onSearchTabChange: (tab: RegionSearchTabsType) => {
            if (tab === RegionSearchTabsType.TRAVEL_TIME) {
                setPreviousDistance(latestQuery.distance);
            }
        }
    });

    const {data: mapDistrictsAndSurroundsData, isLoading: areMapDistrictsAndSurroundingsLoading} = useMapRegionDistrictsAndSurroundings({
        selectedRegions
    });

    const scrollIntoSearchAutocompleteWrapper = () => {
        if (searchAutocompleteWrapperRef.current) {
            const marginOffset = 70;
            const elementTop = searchAutocompleteWrapperRef.current.getBoundingClientRect().top;
            const offsetTop = document.documentElement.scrollTop;
            const scrollToPosition = elementTop + offsetTop - marginOffset;

            window.scrollTo({
                top: scrollToPosition,
                behavior: "smooth"
            });
        }
    };

    useEffect(() => {
        if (!rpFeatureFlagNewUserFlow) {
            return;
        }

        // Don't auto-open location search if filters were restored
        if (filtersRestored) {
            return;
        }

        if (!autoFocusOnInit || guideTourCurrentStepId !== GuideTourStep.SELECT_DISTRICTS || areMapDistrictsAndSurroundingsLoading) {
            return;
        }

        // should be opening and scroll into view shortly after the component is mounted
        if (validateSelectedRegionsWithAvailableDistrictsAndSurroundings(selectedRegions || [], mapDistrictsAndSurroundsData?.results)) {
            setLocationSearchOpenStatus(true);
            addLocationSearchStatusWatcher(() => {
                completeGuideTourStep();
            }, false);
        } else {
            completeGuideTourStep();
        }
    }, [guideTourCurrentStepId, areMapDistrictsAndSurroundingsLoading, filtersRestored]);

    const {isMobileFiltersActive, closeMobileFilterModal, toggleMobileFilterModal, prevFilterState} = useMobileFilters({
        formValues,
        delayAutoOpen: rpFeatureFlagNewUserFlow ? guideTourCurrentStepId !== GuideTourStep.SELECT_FILTERS : true,
        onClose: (makeGtmHit?: boolean) => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
            makeGtmHit && hitFiltersInteraction("close", convertSearchFormToFiltersTracking(formValues));
        }
    });
    const isAbroadOffersView = useOfferListingAbroadView();

    const [windowHeight, setWindowHeight] = useState(0);
    const {isMobile} = useUserDevice();

    useEffect(() => {
        if (!isMobile) {
            setDesktopSearchBlendActive(isLocationSearchOpen);

            if (autoFocusOnInit && isLocationSearchOpen) {
                scrollIntoSearchAutocompleteWrapper();
            }
        }
    }, [isMobile, isLocationSearchOpen]);

    // handle initial focus on search
    const focusOnSearch = (ref: MutableRefObject<HTMLElement | null>, callback?: () => void) => {
        if (location.hash === SEARCH_FORM_FOCUS_HASH && ref.current?.offsetParent) {
            history.replace({hash: ""});
            setLocationSearchOpenStatus(true);
            callback && callback();
        }
    };
    // focus on search
    const onInputMount = (ref: MutableRefObject<HTMLInputElement | null>) => {
        focusOnSearch(ref, () =>
            setTimeout(() => {
                ref.current?.focus();
            }, 0)
        );
    };
    // and mobile focus on search
    const onMobileSearchTriggerMount = (ref: MutableRefObject<HTMLDivElement | null>) => {
        focusOnSearch(ref);
    };

    useEffect(() => {
        // TODO: This condition is to maintain compatibility with old forms and allow building latestQuery during component's lifecycle.
        // In the future it would be better to rewrite whole form defaults logic.
        if (!hasParentBeenInitialized) {
            return;
        }

        const query = latestQuery as Record<string, string>;

        (async () => {
            if (location.pathname === multiRegionLink()) {
                resetMultiRegionSearchForm(query, location.search);
            } else {
                resetSearchForm(query);
            }
        })();
    }, [hasParentBeenInitialized]);

    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */
    const onSubmit = useSearchFormSubmit({
        latestQuery,
        listFriendlyLink: ({friendlySlug, offerListSubFilterSlug}) => {
            if (viewType.current === ViewType.OFFER_LIST_MAP && offerListSubFilterSlug) {
                const subFilterQuery = getOfferListSubFilterParsed(offerListSubFilterSlug);
                return appendQueryString(
                    listFriendlyLink({
                        friendlySlug,
                        offerListSubFilterSlug
                    }),
                    subFilterQuery
                );
            }

            return listFriendlyLink({friendlySlug, offerListSubFilterSlug});
        },
        abroadListFriendlyLink: (params) => {
            return abroadListFriendlyLink && abroadListFriendlyLink(params);
        },
        multiRegionLink
    });

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
        }

        formActions.update(values);
    };
    const onSearchSubmit = (userQueryParams?: {distance?: number}) => {
        onSubmit(formValues, userQueryParams);
    };

    const onSearchFiltersSubmit = () => {
        onSubmit(formValues);
    };

    const {onAfterChange} = useSearchAndFilterUpdate({
        resetPrivilegedOfferList,
        formValues,
        latestQuery,
        onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => onSubmit(formValues, userQueryParams)
    });

    const enableFiltersAutoOpen = useMemo(
        () => (rpFeatureFlagNewUserFlow ? guideTourCurrentStepId === GuideTourStep.SELECT_FILTERS : false),
        [guideTourCurrentStepId]
    );

    const {isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters} = useFilter({
        formValues,
        formActions: searchFormActionsOnly,
        closeMobileFilterModal: closeMobileFilterModal,
        onAfterChange: onAfterChange,
        onSubmit: (formValues: SearchFormValuesType, userQueryParams?: {distance?: number}) => onSubmit(formValues, userQueryParams),
        enableFiltersAutoOpen,
        disableAutoShowcase: disableFilterAutoShowcase,
        onClose: () => {
            if (rpFeatureFlagNewUserFlow) {
                completeGuideTourStep();
            }
        }
    });

    useEffect(() => {
        if (enableFiltersAutoOpen && isDrawerOpen) {
            scrollIntoSearchAutocompleteWrapper();
        }
    }, [isDrawerOpen]);

    const filtersButtonCLick = () => {
        dispatch(filtersModalOpened({filtersModalOpened: true}));
        setDrawerOpen(true);
    };

    /**
     * Handle filters
     */
    const searchFiltersConfig = getSearchFiltersByOfferType(formValues.type?.[0]);
    const {totalCount: numberOfSearchFilters} = useCountSearchFilters(formValues as SearchFormValuesType, searchFiltersConfig);

    const {listUrl, mapUrl} = useOffersViewsUrl();

    const onTravelTimeCancel = () => {
        regionSearchTabChangeHandler(RegionSearchTabsType.LOCATION);
        dispatch(resetPlaceList());
    };

    const renderSearchAutocomplete = (onDropdownStatusChange: (isOpen: boolean) => void, isInModal?: boolean) => {
        if (disableLocationSearch) {
            return null;
        }

        return isAbroadOffersView ? (
            <SearchAutocompleteWithDropdown
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={setLocationSearchOpenStatus}
                formProps={{
                    formValues,
                    formActions,
                    fetchAllSearchLists,
                    latestQuery
                }}
                onAfterChange={onAfterChange}
                onSearchSubmit={onSearchSubmit}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                includeDistanceFilter
                isInModal={isInModal}
                placeholder="Zmień lokalizację"
                showArrowOnMobile
                blockBodyScroll
            />
        ) : (
            <SearchAutocompleteWithRegionAdjustments
                ref={searchAutocompleteWrapperRef}
                activeRegionSearchTab={activeRegionSearchTab}
                defaultDistanceValue={defaultDistanceValue}
                fetchAllSearchLists={fetchAllSearchLists}
                formActions={searchFormActionsOnly}
                formValues={formValues as SearchFormValuesType}
                isInModal={isInModal}
                onAfterChange={onAfterChange}
                onAfterTabSwitch={onAfterTabSwitch}
                onChange={onChange}
                isDropdownOpen={isLocationSearchOpen}
                onDropdownStatusChange={onDropdownStatusChange}
                onInputMount={onInputMount}
                onSearchSubmit={onSearchSubmit}
                onTravelTimeCancel={onTravelTimeCancel}
                setDesktopTypeBlendActive={setDesktopTypeBlendActive}
                slotOrder={slotOrder}
                regionSearchTabChangeHandler={regionSearchTabChangeHandler}
                regionSearchTabs={regionSearchTabs}
                blockBodyScroll
            />
        );
    };

    /**
     * Render
     */
    const defaultDistanceValue = useDefaultDistance(formValues.distance || latestQuery?.distance);
    const mobileFiltersProps = {
        selectedRegionsCount: formValues["search"].regions ? formValues["search"].regions.length : 0,
        selectedFiltersCount: numberOfSearchFilters,
        onToggleSearchClick: toggleLocationSearch,
        onToggleFiltersClick: toggleMobileFilterModal,
        onToggleSortClick: sortActive ? () => setMobileSortActive((prev) => !prev) : null,
        mapUrl: shouldRenderListBackBtn ? mapUrl : undefined,
        listUrl: !shouldRenderListBackBtn ? listUrl : undefined,
        latestQuery,
        onMount: onMobileSearchTriggerMount,
        firstSelectedRegion: formValues.search?.regions?.[0]?.name,
        sortActive
    };

    return (
        <div className={cx(wrapperStyle, className)}>
            <DesktopBlend
                isActive={isDesktopSearchBlendActive || isDesktopTypeBlendActive}
                pageHeight={`${windowHeight}px`}
                top={blendTop || `${searchFormWithDrawerHeight}px`}
                heightDecrease={blendHeightDecrease ? blendHeightDecrease : `${searchFormWithDrawerHeight}px`}
            />

            <Hidden on="desktop" className={css({py: 1.5})}>
                <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
            </Hidden>

            {!isMobile && (
                <div className={searchSectionWrapper}>
                    <Hidden on="mobile">
                        <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
                    </Hidden>

                    <div className={searchFormWrapperStyle}>
                        <div className={searchInputWrapper}>
                            <div id={googlePlacesDivId} />
                            {renderSearchAutocomplete(setLocationSearchOpenStatus)}
                        </div>
                    </div>
                    <div className={filtersButtonsStyle}>
                        <Button size="small" variant="filled_primary" iconLeft={FiltersIcon} className={actionButtonStyle} onClick={filtersButtonCLick}>
                            FILTRY
                            {numberOfSearchFilters > 0 ? (
                                <Badge variant="label_danger" className={filtersCountBadge}>
                                    {numberOfSearchFilters}
                                </Badge>
                            ) : null}
                        </Button>
                        {formValues.search?.regions && <OfferListNotificationsSubscriptionModal variant={NotificationVariant.ICON} />}
                    </div>
                </div>
            )}

            <SearchFilters
                useFilterHook={{isDrawerOpen, setDrawerOpen, closeDrawer, resetFilters}}
                useMobileFilterHook={{isMobileFiltersActive, closeMobileFilterModal, prevFilterState, toggleMobileFilterModal}}
                latestQuery={latestQuery}
                handleSubmit={onSearchSubmit}
                values={formValues as SearchFormValuesType}
                onChange={formActions.update}
                onAfterChange={onAfterChange}
                onSubmit={onSearchFiltersSubmit}
                isAbroad={isAbroadOffersView}
            />

            {isMobile && (
                <>
                    {showTopFilters ? (
                        <MobileTopSearchTriggers
                            forceShowTopSearchTrigger={showMobileTopSearchTrigger}
                            searchFieldPropsValue={formValues.search}
                            {...mobileFiltersProps}
                            isMap={isMap}
                            showLocationSearch
                        />
                    ) : (
                        <MobileSearchTriggers {...mobileFiltersProps} positionFixed hideBottomPadding={hideBottomPadding} />
                    )}
                    {sortActive && (
                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={() => setMobileSortActive(false)}>
                            <OfferListSortMobile
                                name="mobile-sort"
                                onSubmit={() => setMobileSortActive(false)}
                                latestQuerySort={latestQuery.sort as SearchSortString}
                                onMobileResetForm={resetFilters}
                            />
                        </MobileOptionsModal>
                    )}
                    <MobileOptionsModal
                        modalHeading="Wyszukiwanie"
                        isOpen={isLocationSearchOpen}
                        onModalClose={() => {
                            setLocationSearchOpenStatus(false);
                        }}
                    >
                        <div id={googlePlacesDivId} />
                        {renderSearchAutocomplete(setLocationSearchOpenStatus, true)}
                    </MobileOptionsModal>
                </>
            )}
        </div>
    );
};

const searchSectionWrapper = css({
    display: "none",

    md: {
        mx: "auto",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        pt: 2,
        pb: 1,
        gap: 2,
        position: "relative",
        flexGrow: 1,
        flexShrink: 1,
        flexWrap: "wrap"
    }
});

const searchFormWrapperStyle = css({
    display: "flex",
    flex: "1 1"
});

const searchInputWrapper = css({
    display: "none",

    md: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        flex: "1 1"
    }
});

const actionButtonStyle = css({
    width: "18rem",
    overflow: "visible",
    display: "none",

    md: {
        display: "inline-flex"
    }
});

const filtersCountBadge = css({
    position: "absolute",
    right: "-0.5rem",
    top: "-0.5rem"
});

const wrapperStyle = css({
    "&.isInNavigationBar": {
        pb: 0,
        "& > div:nth-of-type(2)": {
            pt: 0
        }
    }
});

const filtersButtonsStyle = css({
    display: "flex",
    gap: 2
});
